/**
 * Sticky control strip of the review detail page — pinned under the topbar so
 * the finish actions, the version switcher, the diff toggle and the fold
 * controls stay reachable however deep the reviewer has scrolled (the finish
 * buttons used to live in the non-sticky page header, forcing a scroll back
 * to the top of long reviews).
 *
 * Purely presentational; every decision lives on the page.
 */
import type { ReactNode } from "react";

import type { AdminReviewVersion } from "../../api/admin";
import { SEND_NEEDS_NO_COMMENTS, type EmailDelivery } from "../../lib/reviewFinish";
import { versionOptionLabel } from "../../lib/reviewVersions";

export type ReviewViewMode = "current" | "version" | "diff";

interface Props {
  /** All snapshots incl. the current one; undefined while loading/404. */
  versions: AdminReviewVersion[] | undefined;
  currentVersion: number;
  mode: ReviewViewMode;
  viewVersion: number | null;
  onVersionSelect: (v: number | null) => void;
  onDiffToggle: () => void;
  hasSections: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  reviewing: boolean;
  /** Draft comments plus the general feedback, if any — what a finish sends. */
  feedbackCount: number;
  /** Email drafts get "Approve & draft"; docs a plain "Approve". */
  isEmail: boolean;
  /** A briefing is never approved – its one action here is "Send comments";
   * the meeting ends it (the page's close panel). */
  isBriefing?: boolean;
  /** The envelope names one of the owner's mailboxes the server sends from. */
  canSend: boolean;
  /** Why approving is not possible yet (an unsigned attachment), or nothing. */
  approveBlocked?: string | null;
  finishPending: boolean;
  onFinish: (decision: "request_changes" | "approve", delivery?: EmailDelivery) => void;
  /** Snooze control, rendered before the finish actions. */
  snooze?: ReactNode;
}

export function ReviewToolbar({
  versions,
  currentVersion,
  mode,
  viewVersion,
  onVersionSelect,
  onDiffToggle,
  hasSections,
  onExpandAll,
  onCollapseAll,
  reviewing,
  feedbackCount,
  isEmail,
  isBriefing = false,
  canSend,
  approveBlocked,
  finishPending,
  onFinish,
  snooze,
}: Props) {
  const hasHistory = (versions?.length ?? 0) > 1;
  const diffFrom = viewVersion ?? currentVersion - 1;

  return (
    <div className="review-toolbar">
      {hasHistory ? (
        <>
          <select
            className="input font-mono text-xs tnum w-auto"
            value={mode === "version" && viewVersion != null ? viewVersion : currentVersion}
            onChange={(e) => {
              const v = Number(e.target.value);
              onVersionSelect(v === currentVersion ? null : v);
            }}
            aria-label="Version"
          >
            {(versions ?? []).map((v) => (
              <option key={v.content_version} value={v.content_version}>
                {versionOptionLabel(v, currentVersion)}
              </option>
            ))}
          </select>
          {currentVersion > 1 ? (
            <button
              type="button"
              className="btn btn-sm btn-ghost font-mono"
              aria-pressed={mode === "diff"}
              onClick={onDiffToggle}
            >
              ± changes since v{diffFrom}
            </button>
          ) : null}
        </>
      ) : null}

      {hasSections && mode === "current" ? (
        <>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onExpandAll}>
            Expand all
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onCollapseAll}>
            Collapse all
          </button>
        </>
      ) : null}

      {reviewing && isBriefing ? (
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className="btn btn-main"
            disabled={finishPending || feedbackCount === 0}
            title={feedbackCount === 0 ? "Add a comment first" : undefined}
            onClick={() => onFinish("request_changes")}
          >
            Send comments
          </button>
        </div>
      ) : reviewing ? (
        <div className="ml-auto flex items-center gap-2">
          {snooze}
          <button
            type="button"
            className="btn btn-second"
            disabled={finishPending || feedbackCount === 0}
            title={
              feedbackCount === 0
                ? isEmail ? "Add a comment or general feedback first" : "Add a comment first"
                : undefined
            }
            onClick={() => onFinish("request_changes")}
          >
            Request changes
          </button>
          {isEmail ? (
            <>
              <button
                type="button"
                className={canSend && feedbackCount === 0 ? "btn btn-second" : "btn btn-main"}
                disabled={finishPending || !!approveBlocked}
                title={approveBlocked ?? (feedbackCount ? "Your comments are worked in, then the draft is created" : undefined)}
                onClick={() => onFinish("approve", "draft")}
              >
                Approve &amp; draft
              </button>
              {canSend ? (
                <button
                  type="button"
                  className={feedbackCount === 0 ? "btn btn-main" : "btn btn-second"}
                  disabled={finishPending || !!approveBlocked || feedbackCount > 0}
                  title={approveBlocked ?? (feedbackCount ? SEND_NEEDS_NO_COMMENTS : undefined)}
                  onClick={() => onFinish("approve", "send")}
                >
                  Approve &amp; send
                </button>
              ) : null}
            </>
          ) : (
            <button
              type="button"
              className="btn btn-main"
              disabled={finishPending}
              onClick={() => onFinish("approve")}
            >
              Approve
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
